import { type VirtualItem, useVirtualizer } from '@tanstack/react-virtual'
import type React from 'react'
import { type CSSProperties, useCallback, useEffect, useState } from 'react'

/**
 * Windowing primitives for option lists (dropdowns, filter value pickers) that can hold far more
 * rows than are worth mounting. Split in two so callers take only what they need:
 *
 * - `useVirtualOptionRows` — the rendering window. Use when the host already owns the focused row.
 * - `useOptionListKeyboard` — focus index and key handling, layered on top for hosts that don't.
 *
 * Both deliberately avoid the two mistakes that windowed lists keep repeating: resetting scroll
 * when the list merely *grows* (which fights pagination), and scrolling on every focus change
 * (which lets a resting mouse cursor hijack keyboard navigation).
 */

const DEFAULT_OVERSCAN = 8
const DEFAULT_REACH_END_ROWS = 6

interface UseVirtualOptionRowsParams {
	count: number
	/**
	 * Row height in px. Exact when rows are single-line (`truncate`); an estimate when `measure` is
	 * on.
	 */
	rowHeight: number
	/**
	 * Measure each row instead of trusting `rowHeight`. Only for rows that genuinely wrap — it costs
	 * a ResizeObserver per mounted row plus a measurement cache that grows with how far the user
	 * scrolls, which is what makes very large lists judder. Prefer single-line rows.
	 */
	measure?: boolean
	/**
	 * Stable identity per row. Without it the virtualizer keys by index, and index→item mapping
	 * changes on every keystroke once the list is filtered.
	 */
	getItemKey?: (index: number) => string | number
	overscan?: number
	/**
	 * Scrolls back to the top when this changes. Key it on the search query / open state — never on
	 * the list length, or appending a page of results yanks the user back to the top.
	 */
	resetKey?: string
	/** Called when the user scrolls within `reachEndRows` of the bottom. */
	onReachEnd?: () => void
	reachEndRows?: number
}

interface VirtualOptionRows {
	/**
	 * Callback ref for the scroll container. It's state rather than a ref because Radix mounts
	 * popover content on its own delayed render — a plain ref wouldn't re-render this hook's host,
	 * and the virtualizer would measure nothing and yield zero rows.
	 */
	setScrollElement: (element: HTMLDivElement | null) => void
	virtualItems: VirtualItem[]
	totalSize: number
	handleScroll: (event: React.UIEvent<HTMLDivElement>) => void
	scrollToIndex: (index: number) => void
	rowStyle: (virtualRow: VirtualItem) => CSSProperties
	/**
	 * Ref for each row element when `measure` is on, otherwise undefined. Rows carrying it must also
	 * set `data-index={virtualRow.index}` — that's how the virtualizer maps an element back to a row.
	 */
	measureRow: ((element: HTMLElement | null) => void) | undefined
}

export function useVirtualOptionRows({
	count,
	rowHeight,
	measure = false,
	getItemKey,
	overscan = DEFAULT_OVERSCAN,
	resetKey,
	onReachEnd,
	reachEndRows = DEFAULT_REACH_END_ROWS,
}: UseVirtualOptionRowsParams): VirtualOptionRows {
	const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)

	const virtualizer = useVirtualizer({
		count,
		getScrollElement: () => scrollElement,
		estimateSize: () => rowHeight,
		getItemKey,
		overscan,
	})

	useEffect(() => {
		if (resetKey === undefined) {
			return
		}
		scrollElement?.scrollTo({ top: 0 })
	}, [resetKey, scrollElement])

	const handleScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			if (!onReachEnd) {
				return
			}
			const el = event.currentTarget
			if (el.scrollHeight - el.scrollTop - el.clientHeight < rowHeight * reachEndRows) {
				onReachEnd()
			}
		},
		[onReachEnd, rowHeight, reachEndRows],
	)

	const scrollToIndex = useCallback(
		(index: number) => {
			if (index >= 0) {
				virtualizer.scrollToIndex(index, { align: 'auto' })
			}
		},
		[virtualizer],
	)

	const rowStyle = useCallback(
		(virtualRow: VirtualItem): CSSProperties => ({
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			// Pinning the height would defeat measurement, so leave it to content when measuring.
			...(measure ? {} : { height: virtualRow.size }),
			transform: `translateY(${virtualRow.start}px)`,
		}),
		[measure],
	)

	return {
		setScrollElement,
		virtualItems: virtualizer.getVirtualItems(),
		totalSize: virtualizer.getTotalSize(),
		handleScroll,
		scrollToIndex,
		rowStyle,
		measureRow: measure ? virtualizer.measureElement : undefined,
	}
}

interface UseOptionListKeyboardParams {
	count: number
	isDisabled?: (index: number) => boolean
	/** Re-highlights the first enabled row when this changes — the search query / open state. */
	resetKey: string
	/**
	 * Called for keyboard-driven focus moves only, so a row sliding under a resting cursor can't
	 * scroll the list.
	 */
	onScrollToIndex?: (index: number) => void
}

interface OptionListKeyboard {
	focusedIndex: number
	/** Highlight from hover. Deliberately does not scroll. */
	focusByPointer: (index: number) => void
	/** Returns true when the key was consumed (and `preventDefault` already called). */
	handleKeyDown: (event: React.KeyboardEvent) => boolean
}

export function useOptionListKeyboard({
	count,
	isDisabled,
	resetKey,
	onScrollToIndex,
}: UseOptionListKeyboardParams): OptionListKeyboard {
	const firstEnabled = useCallback((): number => {
		let index = 0
		while (index < count && isDisabled?.(index)) {
			index++
		}
		return index < count ? index : -1
	}, [count, isDisabled])

	const [focusedIndex, setFocusedIndex] = useState(-1)

	// Derived-state reset: recomputing during render (rather than in an effect keyed on the list
	// length) is what keeps pagination from stealing the highlight every time a page lands.
	const [prevResetKey, setPrevResetKey] = useState<string | null>(null)
	if (resetKey !== prevResetKey) {
		setPrevResetKey(resetKey)
		setFocusedIndex(firstEnabled())
	} else if (focusedIndex >= count || (focusedIndex === -1 && count > 0)) {
		// Rows can appear after the list opens (async results) or disappear under it (changed
		// exclusions), leaving the highlight missing or past the end. Re-derive only in those two
		// cases — re-deriving whenever the list grows is what makes pagination steal the highlight.
		setFocusedIndex(firstEnabled())
	}

	const moveFocus = useCallback(
		(from: number, direction: 1 | -1): number => {
			if (count === 0) {
				return -1
			}
			let index = from === -1 ? (direction === 1 ? 0 : count - 1) : from + direction
			while (index >= 0 && index < count && isDisabled?.(index)) {
				index += direction
			}
			return index >= 0 && index < count ? index : from
		},
		[count, isDisabled],
	)

	const edgeFocus = useCallback(
		(edge: 'first' | 'last'): number => {
			if (count === 0) {
				return -1
			}
			if (edge === 'first') {
				return firstEnabled()
			}
			let index = count - 1
			while (index >= 0 && isDisabled?.(index)) {
				index--
			}
			return index
		},
		[count, isDisabled, firstEnabled],
	)

	const applyFocus = useCallback(
		(next: number) => {
			if (next < 0) {
				return
			}
			setFocusedIndex(next)
			onScrollToIndex?.(next)
		},
		[onScrollToIndex],
	)

	// Key set matches what cmdk handles, so lists that opt into windowing keep the same feel as the
	// cmdk-backed ones elsewhere in the app (including the ctrl-n/p/j/k vim bindings).
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent): boolean => {
			const { key, ctrlKey, metaKey } = event
			const isDown = key === 'ArrowDown' || (ctrlKey && (key === 'n' || key === 'j'))
			const isUp = key === 'ArrowUp' || (ctrlKey && (key === 'p' || key === 'k'))

			if (isDown) {
				event.preventDefault()
				applyFocus(metaKey ? edgeFocus('last') : moveFocus(focusedIndex, 1))
				return true
			}
			if (isUp) {
				event.preventDefault()
				applyFocus(metaKey ? edgeFocus('first') : moveFocus(focusedIndex, -1))
				return true
			}
			if (key === 'Home') {
				event.preventDefault()
				applyFocus(edgeFocus('first'))
				return true
			}
			if (key === 'End') {
				event.preventDefault()
				applyFocus(edgeFocus('last'))
				return true
			}
			return false
		},
		[applyFocus, edgeFocus, moveFocus, focusedIndex],
	)

	return { focusedIndex, focusByPointer: setFocusedIndex, handleKeyDown }
}
